/**
 * SDK contract for `CategoryPageBelowList` (ECOMM-13290 / PF-9650).
 *
 * The registry key itself needs to be added to the published
 * `@dutchiesdk/ecommerce-extensions-sdk` package's `RemoteModuleRegistry`
 * type (that package's source isn't part of this monorepo). Because
 * `RemoteModuleRegistry` is a `type` alias (not an `interface`), it can't be
 * extended here via TS declaration merging, so this file defines the entry
 * shape locally and an `ExtendedRemoteModuleRegistryWithBelowList` intersection type used
 * where the loaded remote module is read (see remote-theme.tsx). Once
 * the SDK package publishes `CategoryPageBelowList` on `RemoteModuleRegistry`
 * directly, this local temporary type extension can be deleted in favor of the real type.
 */

import React from 'react';
import type {
  ListPageEntry,
  RemoteBoundaryComponent,
  RemoteModuleRegistry,
} from '@dutchiesdk/ecommerce-extensions-sdk';
import { getCategoryToMatch } from './category-page-subheaders';

export { getCategoryToMatch };

/** Uses the same category-key contract as the SDK's existing list-page registries. */
export type CategoryPageBelowListCategory = NonNullable<ListPageEntry['category']>;

export type CategoryPageBelowListEntry = {
  /** Optional top-level category route key (e.g. 'flower', 'edibles'). */
  category?: CategoryPageBelowListCategory;
  /** Optional subcategory route key (e.g. 'indica', 'gummies'). */
  subcategory?: string;
  /** Optional collection route slug / identifier (e.g. 'staff-picks', '420-deals'). */
  collection?: string;
  /** Required. Array of remote components rendered below the category product list. */
  components: RemoteBoundaryComponent[];
};

export type CategoryPageBelowListIterable = {
  category?: CategoryPageBelowListCategory;
  subcategory?: string;
  collection?: string;
  components: React.ElementType[];
};

/**
 * Local stand-in for `RemoteModuleRegistry` with `CategoryPageBelowList`
 * added.
 */
export type ExtendedRemoteModuleRegistryWithBelowList = RemoteModuleRegistry & {
  CategoryPageBelowList?: CategoryPageBelowListEntry[];
};

export type CategoryPageBelowListMatchTarget = {
  category?: string;
  subcategory?: string;
  collection?: string;
};

/**
 * Resolves the matching components for the current PLP (category, subcategory, or collection),
 * or `[]` when there's no match or invalid configuration.
 *
 * Precedence / matching rules:
 * 1. Subcategory PLP (`target.subcategory` present): matches entry where `entry.subcategory === target.subcategory`.
 *    If `entry.category` is also specified, both must match.
 * 2. Collection PLP (`target.collection` present): matches entry where `entry.collection === target.collection`.
 * 3. Top-level category PLP (`target.category` present without subcategory or collection): matches entry where
 *    `entry.category === target.category` (and no subcategory or collection constraint on the entry).
 *
 * Duplicate matches select the first matching entry.
 * Fails closed to `[]` on missing/unmatched/empty/malformed entries.
 */
export const findMatchingCategoryPageBelowList = (
  entries: unknown,
  target: CategoryPageBelowListMatchTarget | string | undefined
): React.ElementType[] => {
  if (!Array.isArray(entries) || entries.length === 0 || !target) {
    return [];
  }

  // Support string argument for backwards compatibility (top-level category name)
  const normalizedTarget: CategoryPageBelowListMatchTarget = typeof target === 'string' ? { category: target } : target;

  const { category, subcategory, collection } = normalizedTarget;

  if (!category && !subcategory && !collection) {
    return [];
  }

  const match: unknown = entries.find((entry: unknown) => {
    if (entry === null || typeof entry !== 'object') {
      return false;
    }

    const candidate = entry as Partial<CategoryPageBelowListEntry>;

    // Case 1: Subcategory route
    if (subcategory) {
      if (candidate.subcategory !== subcategory) {
        return false;
      }
      // If candidate also scopes to a specific category, both must match
      if (candidate.category && candidate.category !== category) {
        return false;
      }
      return true;
    }

    // Case 2: Collection route
    if (collection) {
      return candidate.collection === collection;
    }

    // Case 3: Top-level category route (no subcategory or collection on route)
    if (category) {
      // Must not match an entry specifically intended for a subcategory or collection
      if (candidate.subcategory || candidate.collection) {
        return false;
      }
      return candidate.category === category;
    }

    return false;
  });

  if (match === null || typeof match !== 'object' || !('components' in match) || !Array.isArray(match.components)) {
    return [];
  }

  const { components } = match;
  return components.filter((comp): comp is React.ElementType => comp !== null && comp !== undefined);
};
